import { QuoteInfo } from "../../api/types/search-quote-response";
import { UpsertPartnerQuoteInfoRequest } from "../../api/types/upsert-partner-quote-info-request";

export const mapTelematicsBooleanToString = (value?: boolean | null): string => {
    if (value === true) return "yes";
    if (value === false) return "no";
    return "";
};

export const safeConvertDate = (dateString: string | null | undefined): string => {
    if (!dateString || dateString.trim() === '') return '';
    try {
        if (/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(dateString)) return dateString;
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return '';
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const day = date.getDate().toString().padStart(2, '0');
        const year = date.getFullYear();
        return `${month}/${day}/${year}`;
    } catch {
        return '';
    }
};

export function mapQuoteInfoToUpsertInputs(quoteInfo: QuoteInfo): Partial<UpsertPartnerQuoteInfoRequest> {
    const baseFields = {
        lineOfBusiness: quoteInfo.lineOfBusiness?.[0] ?? '',
        stateCode: quoteInfo.stateCode ?? '',
        emailId: quoteInfo.email ?? '',
        firstName: quoteInfo.firstName ?? '',
        lastName: quoteInfo.lastName ?? '',
        zipCode: quoteInfo.zipCode ?? '',
        dateOfBirth: quoteInfo.dateOfBirth ?? '',
        phoneNumber: quoteInfo.phoneNumber ?? '',
        referenceNumber: quoteInfo.referenceNumber ?? '',
        source: quoteInfo.source ?? '',
        userType: quoteInfo.userType ?? '',
        premium: quoteInfo.premium ?? '',
        firstApplicationFormID: quoteInfo.firstApplicationFormID ?? '',
        token: quoteInfo.token ?? '',
        businessName: quoteInfo.businessName ?? '',
        businessAddress: quoteInfo.businessAddress ?? '',
        businessClassDescription: quoteInfo.businessClassDescription ?? '',
        quoteUpdatedDate: safeConvertDate(quoteInfo.quoteUpdatedDate),
        quoteCreatedDate: safeConvertDate(quoteInfo.quoteCreatedDate),
    };

    const quoteOutcomeFields = quoteInfo.quoteOutcome ? {
        quoteOutcome: quoteInfo.quoteOutcome.quoteOutcomeType ?? '',
        primaryOutcomeType: quoteInfo.quoteOutcome.primaryOutcomeType ?? '',
        secondaryOutcomeType: quoteInfo.quoteOutcome.secondaryOutcomeType ?? '',
        soldPremium: quoteInfo.quoteOutcome.soldPremium ?? '',
        paymentConfirmationNumber: quoteInfo.quoteOutcome.paymentConfirmationNumber ?? '',
        policyNumber: quoteInfo.quoteOutcome.policyNumber ?? '',
        leadSource: quoteInfo.quoteOutcome.leadSource ?? '',
        language: quoteInfo.quoteOutcome.language ?? '',
        effectiveDate: safeConvertDate(quoteInfo.quoteOutcome.effectiveDate),
        expirationDate: safeConvertDate(quoteInfo.quoteOutcome.expirationDate),
        salesDate: safeConvertDate(quoteInfo.quoteOutcome.salesDate),
        paymentPlan: quoteInfo.quoteOutcome.paymentPlan ?? '',
        isEndorsement: quoteInfo.quoteOutcome.isEndorsement ?? false,
        hasElectronicLoggingDevice: mapTelematicsBooleanToString(quoteInfo.quoteOutcome.hasElectronicLoggingDevice),
        telematicsProgramType: quoteInfo.quoteOutcome.telematicsProgramType ?? "",
        telematicsSelection: mapTelematicsBooleanToString(quoteInfo.quoteOutcome.telematicsSelection),
    } : {};

    const disclosureFields = quoteInfo.quoteDisclosureDetails ? {
        disclosuresRead: quoteInfo.quoteDisclosureDetails.disclosuresRead ?? [],
        disclosuresSelectedAsYes: quoteInfo.quoteDisclosureDetails.disclosuresWithYesSelection ?? [],
        disclosuresSelectedAsNo: quoteInfo.quoteDisclosureDetails.disclosuresWithNoSelection ?? [],
        disclosuresSelectedAsNA: quoteInfo.quoteDisclosureDetails.disclosuresMarkedAsNotApplicable ?? [],
    } : {};

    return {
        ...baseFields,
        ...quoteOutcomeFields,
        ...disclosureFields,
    };
}
